import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useField } from 'informed';
import { ReactDatePickerProps } from 'react-datepicker';

import { OmegaDatePicker } from '../OmegaDatePicker';

import { Error, FieldContainer, Label } from './styledComponents';

export interface FilterType {
  label: string;
  value: string;
}

interface InformedOmegaDatePickerPropTypes
  extends Omit<
    ReactDatePickerProps,
    | 'showTimeInput'
    | 'showTimeSelectOnly'
    | 'timeFormat'
    | 'withPortal'
    | 'useWeekdaysShort'
  > {
  field: string;
  validationSchemaObject: any;
  error: string;
  touched: boolean;
  label?: string;
  inputProps?: any;
  filters?: Array<FilterType>;
  onClickFilter?: (value: string) => void;
  selectedFilterValue?: string;
  headerDisplayFormat?: string;
  dateFormat?: string;
  helpText?: string;
  placeholder?: string;
  reRenderedFieldsObjects?: any;
  showMonthDropdown?: boolean;
  minDate?: any;
  containerCSS?: React.CSSProperties;
  errorClassName?: string;
  onSelectedDate?: (value) => void;
  isHidden?: boolean;
  disabled?: boolean;
  enableDataMask?: boolean;
  isDependent?: boolean;
  dependentFieldValue?: string;
  labelStyles?: string;
  isMarginBottomZero?: boolean;
}

export const InformedOmegaDatePicker = (
  props: InformedOmegaDatePickerPropTypes
) => {
  const {
    field,
    validationSchemaObject,
    error,
    touched,
    label,
    selected,
    onCalendarOpen,
    dateFormat = 'yyyy-MM-dd',
    showTimeSelect,
    popperClassName,
    weekDayClassName,
    dayClassName,
    timeClassName,
    selectsRange,
    startDate,
    endDate,
    inputProps,
    filters,
    onClickFilter,
    headerDisplayFormat,
    selectedFilterValue,
    locale,
    shouldCloseOnSelect = true,
    helpText,
    placeholder,
    reRenderedFieldsObjects,
    showMonthDropdown,
    minDate,
    maxDate,
    containerCSS,
    errorClassName,
    onSelectedDate,
    isHidden,
    disabled,
    enableDataMask,
    dependentFieldValue,
    isDependent,
    labelStyles,
    isMarginBottomZero
  } = props;

  const { fieldState, informed, fieldApi } = useField({
    field,
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    label,
    ...reRenderedFieldsObjects
  });

  const { setValue, setTouched } = fieldApi;

  const [selectedValue, setSelectedValue] = useState('');

  useEffect(() => {
    if (isDependent) {
      setValue('');
      setSelectedValue('');
    }
  }, [dependentFieldValue]);

  const onChange = (value) => {
    setSelectedValue(value);
    setValue(format(value, dateFormat));
    setTouched(true);
    onSelectedDate && onSelectedDate(value);
  };

  const renderDatePickerField = () => (
    <OmegaDatePicker
      {...props}
      dateFormat={dateFormat}
      value={fieldState.value as string}
      onChange={onChange}
      selected={selectedValue as any}
      shouldCloseOnSelect={shouldCloseOnSelect}
      inputProps={{ errorMessage: error, hint: helpText }}
      placeholder={placeholder}
      error={error}
      disabled={disabled}
      enableDataMask={enableDataMask}
    />
  );
  return (
    <FieldContainer css={containerCSS} isHidden={isHidden}>
      <Label className={labelStyles}>{label}</Label>
      {renderDatePickerField()}
      <Error className={errorClassName}>{error}</Error>
    </FieldContainer>
  );
};
